// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import last from 'lodash/last';
import isNil from 'lodash/isNil';
import {VNode} from 'snabbdom';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {KEY_VALUES, rafRetry, shadowContains} from 'common/util/dom';
import {ItemClickEvent, Section} from 'common/components/items-menu/types';
import {Visibility, VisibilityChangeEvent} from 'common/components/base/stackable';

import '../tag';
import {InputAttrs, InputComponent} from '../input';
import css from './index.inline.styl';
import {template} from './template';
import {TagItem} from './tag-item';
import {TagInputChangeAction, TagInputJsxProps, TagInputSearchMode, TagInputState} from './types';

function getTagId(tag: TagItem): string {
  return tag.value ?? tag.label;
}

export class TagInput extends Component<TagInputState, TagInputJsxProps[`attrs`]> {
  static get attrsSchema(): AttrsSchema<TagInputJsxProps[`attrs`]> {
    return {
      ...InputComponent.attrsSchema,
      'min-tags': {type: `number`, default: 1},
      // TODO: refactor to remove `tags` and instead pass isSelected/isDisabled via `menuSections`
      tags: {
        type: `json`,
        default: [],
        description: `interface: TagItem[], with a label and optional isDisabled
        and attrs params (attrs gets passed down to the mp-tag attrs)`,
      },
      'menu-width': {
        type: `number`,
        default: 0,
        description: `Width of drop menu in pixels ({width: px}). Value of "0" will inherit use the width of the input. Value of "null" will not set any width, allowing the menu to grow to its contents.`,
      },
      'search-mode': {type: `string`, default: TagInputSearchMode.Text},
      'mp-items-menu-attrs': `json`,
      multiline: {
        type: `boolean`,
        description: `If there's too many tags to fit on one line, this determines whether the tags will render 1) on a single line with scroll or 2) wrap onto multiple lines.`,
      },
      'controlled-mode': {
        type: `boolean`,
        description: `If enabled, it is up to the parent component to update tags and menuSection (rather than having it managed by this component)`,
      },
    };
  }

  get config() {
    return {
      defaultState: {
        inputText: ``,
        tagMap: new Map(),
        tagMenu: null,
        menuSections: null,
        isMenuOpen: null,
        isFocused: null,
        suffixRenderer: null,
        sectionsFromInputFn: null,
      },
      css,
      template,
      useShadowDom: true,
    };
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === `tags`) {
      this.update({
        tagMap: new Map(this.attr(`tags`).map((tag) => [getTagId(tag), tag])),
      });
    }
  }

  getInputAttrs(): InputAttrs {
    const inputAttrs = omit(
      this.attrs(),
      `min-tags`,
      `tags`,
      `menu-width`,
      `disable-input`,
      `search-mode`,
      `mp-items-menu-attrs`,
      `multiline`,
    );

    return {
      ...omitBy(inputAttrs, isNil),
      ...(this.attr(`multiline`) && {'multiline-prefix': this.attr(`multiline`)}),
    };
  }

  focus() {
    rafRetry(() => {
      this.state.searchInputEl?.focus();
      return Boolean(this.state.searchInputEl);
    });
  }

  set suffixRenderer(suffixRenderer: () => VNode | VNode[]) {
    this.update({suffixRenderer});
  }

  set value(value: string) {
    this.update({inputText: value});
  }

  set sectionsFromInputFn(sectionsFromInputFn: TagInputJsxProps[`props`][`sectionsFromInputFn`]) {
    this.update({sectionsFromInputFn});
  }

  set menuSections(menuSections: Array<Section<TagItem>>) {
    if (isEqual(this.state.menuSections, menuSections)) {
      return;
    }

    this.update({
      menuSections,
      isMenuOpen: false,
    });
  }

  canRemoveTag(tag: TagItem) {
    return this.state.tagMap.size > this.attr(`min-tags`) && !tag.isDisabled;
  }

  removeTag(tag: TagItem, {focusOnChange = true}: {focusOnChange?: boolean} = {}): void {
    const tagMap = this.state.tagMap;
    tagMap.delete(getTagId(tag));
    this.update({tagMap});

    this.dispatchEvent(new CustomEvent(`change`, {detail: {tag, action: TagInputChangeAction.RemoveTag}}));

    if (tagMap.size === 0 || focusOnChange) {
      requestAnimationFrame(() => this.focus());
    }
  }

  removeLastTag(): void {
    const {tagMap} = this.state;
    const tagList = Array.from(tagMap.values());
    for (let i = tagList.length - 1; i >= 0; i--) {
      if (this.canRemoveTag(tagList[i])) {
        this.removeTag(tagList[i]);
        break;
      }
    }
  }

  getPlaceholder() {
    if (this.state.tagMap.size) {
      return ``;
    }

    return this.attr(`placeholder`) || ``;
  }

  getMenuSections(): Array<Section<TagItem>> {
    if (this.state.menuSections === null) {
      return null;
    }

    return this.state.menuSections.map((section) => {
      return {
        ...section,
        items: section.items.map((item: any) => {
          return {
            // before item destructuring to allow callsite to override elref
            elref: `mpTagInputTagMenuItem`,
            isDisabled: this.state.tagMap.has(getTagId(item)),
            ...item,
          };
        }),
      };
    });
  }

  getStagedTagSearch(): string | null {
    if ([TagInputSearchMode.Text, TagInputSearchMode.Combobox].includes(this.attr(`search-mode`))) {
      return this.state.inputText;
    }
    const lastWord = last(this.state.inputText.split(/\s+/));
    return lastWord && lastWord.startsWith(`#`) ? lastWord.slice(1) : null;
  }

  getDropMenuStyles() {
    const searchInputWidth = this.state.searchInputEl?.clientWidth;
    const baseStyles = {maxWidth: `${searchInputWidth}px`};

    let menuWidth = this.attr(`menu-width`);

    if (isNil(menuWidth)) {
      return baseStyles;
    }

    if (menuWidth === 0) {
      menuWidth = searchInputWidth;
    }

    return {
      ...baseStyles,
      width: `${menuWidth}px`,
    };
  }

  handleSearchInputInsert({elm}: {elm: Node}) {
    this.update({searchInputEl: elm as InputComponent});
  }

  setTagMenuOpen(isOpen: boolean): void {
    this.update({isMenuOpen: isOpen});
  }

  handleTagMenuChange(ev: VisibilityChangeEvent): void {
    ev.stopPropagation();
    if (ev.detail.state === Visibility.Closed) {
      this.setTagMenuOpen(false);
    }
  }

  handleClickTagItem(ev: ItemClickEvent) {
    let updatedInputText: string;
    if (this.attr(`search-mode`) !== TagInputSearchMode.Combobox) {
      if (this.attr(`search-mode`) === TagInputSearchMode.Text) {
        updatedInputText = ``;
      } else {
        updatedInputText = this.state.inputText.replace(
          // remove tag at the very end of searchFilter string
          new RegExp(`#${this.getStagedTagSearch()}$`),
          ``,
        );
      }
      this.changeInputText(updatedInputText);
    }
    this.setTagMenuOpen(false);
    if (this.attr(`search-mode`) !== TagInputSearchMode.Combobox) {
      this.state.searchInputEl.focus();
    }

    if (!this.attr(`controlled-mode`)) {
      const tag = ev.detail.item.value;
      const tagMap = this.state.tagMap;
      tagMap.set(getTagId(tag), tag);
      this.update({tagMap});
    }

    this.dispatchEvent(new CustomEvent(`clickMenuItem`, ev));
  }

  changeInputText(inputText: string): void {
    this.update({inputText});
    if (this.state.menuSections) {
      this.setTagMenuOpen(typeof this.getStagedTagSearch() === `string`);
    }
    this.dispatchEvent(
      new CustomEvent(`change`, {detail: {value: this.state.inputText, action: TagInputChangeAction.Text}}),
    );
  }

  handleClearInput(ev: CustomEvent) {
    ev.stopPropagation();
    this.update({
      inputText: ``,
      tagMap: new Map(),
    });
    this.dispatchEvent(new CustomEvent(`clear`));
    this.focus();
  }

  handleInputFocus() {
    if (this.attr(`search-mode`) !== TagInputSearchMode.Hashtag) {
      this.setTagMenuOpen(true);
    }
    this.update({isFocused: true});
  }

  handleInputBlur() {
    this.update({isFocused: false});
  }

  handleTagFocus(ev: FocusEvent) {
    (ev.target as HTMLElement).scrollIntoView();
    this.update({isFocused: true});
    ev.stopPropagation();
  }

  handleTagBlur(ev: FocusEvent) {
    // For unknown reason, if the tag is blurred because of focusing the input, the input's `focus` event is not fired.
    // For this case, we shouldn't set `isFocused` to false.
    if (!ev.relatedTarget || !shadowContains(this.state.searchInputEl, ev.relatedTarget as HTMLElement)) {
      this.update({isFocused: false});
    }
    ev.stopPropagation();
  }

  handleInputKeyDown(ev: KeyboardEvent) {
    const {tagMap} = this.state;
    const target = ev.target as InputComponent;

    /** remove last tag before input cursor */
    if (ev.code === KEY_VALUES.BACKSPACE && target.selectionStart === 0) {
      /** delete last tag when cursor is at beginning of input */
      this.removeLastTag();
    } else if (ev.code === KEY_VALUES.ARROW_LEFT && target.selectionStart === 0) {
      /** focus last tag before input cursor */
      this.focusTagAtIndex(tagMap.size - 1);
    } else if (
      /** open dropdown on key down if search tags are passed */
      ev.code === KEY_VALUES.ARROW_DOWN &&
      this.state.menuSections &&
      Boolean(this.state.isMenuOpen) === false &&
      this.attr(`search-mode`) !== TagInputSearchMode.Hashtag
    ) {
      this.setTagMenuOpen(true);
    }
  }

  /**
   * Handles interactions on tags like deleting & navigating with arrows
   */
  handleTagKeyDown = (ev: KeyboardEvent, {tag, index}: {tag: TagItem; index: number}) => {
    const handleKey = () => {
      ev.preventDefault();
      ev.stopPropagation();
    };

    if (ev.code === KEY_VALUES.ESCAPE) {
      /** focus input on blur */
      handleKey();
      this.focus();
    } else if (ev.code === KEY_VALUES.BACKSPACE) {
      /** delete tag on backspace */
      handleKey();
      if (this.canRemoveTag(tag)) {
        const isLastTag = index === this.state.tagMap.size - 1;
        this.removeTag(tag, {focusOnChange: isLastTag});
      }
    } else if (ev.code === KEY_VALUES.ARROW_RIGHT) {
      /** focus next tag on right arrow, fallback to focus input */
      handleKey();
      const nextEl = this.getTagElAtIndex(index + 1);

      if (nextEl) {
        nextEl.focus();
      } else {
        this.focus();
      }
    } else if (ev.code === KEY_VALUES.ARROW_LEFT) {
      /** focus previous tag on left arrow */
      handleKey();
      this.focusTagAtIndex(index - 1);
    }
  };

  getTagElAtIndex(index: number): HTMLElement | undefined {
    return this.state.searchInputEl?.el?.querySelector<HTMLElement>(`mp-tag[data-index="${index}"]`);
  }

  focusTagAtIndex(index: number): void {
    this.getTagElAtIndex(index)?.focus();
    if (index === 0) {
      // Focusing tag at index 0 seems to cause part of left side of tag highlight to be cut off.
      // Explicitly scrolling to start of input wrapper to fix this
      this.state.searchInputEl?.scrollToStart();
    }
  }
}

export const TagInputEl = defineAndMakeJsxFactory<TagInputJsxProps>(`mp-tag-input`, TagInput);
export const TagInputReact = panel2react<TagInputJsxProps>(`mp-tag-input`, TagInput);
